import React, { useState, useEffect } from 'react';
import { User, CreditCard, Shield, CheckCircle, Loader2, AlertCircle, X, Sparkles, Crown, Zap, Receipt, Calendar, Download, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import api, { clearTokens } from '../../../api';
import toast from 'react-hot-toast';
import Modal from '../../common/Modal';

// Billing plans configuration
const BILLING_PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    interval: 'forever',
    currency: 'NGN',
    description: 'Perfect for getting started',
    features: [
      'Up to 5 forms',
      '100 responses/month',
      'Basic analytics',
      'Email support'
    ],
    highlighted: false,
    showInBoth: true // Show in both monthly and yearly views
  },
  {
    id: 'creator_monthly',
    name: 'Creator',
    price: 4800,
    interval: 'month',
    currency: 'NGN',
    description: 'For growing creators',
    features: [
      'Unlimited forms',
      'Unlimited responses',
      'Advanced analytics',
      'Priority support',
      'Custom branding',
      'Export to CSV/Excel'
    ],
    highlighted: true
  },
  {
    id: 'creator_yearly',
    name: 'Creator',
    price: 48000,
    interval: 'year',
    currency: 'NGN',
    description: 'Best value - Save ₦9,600',
    features: [
      'Unlimited forms',
      'Unlimited responses',
      'Advanced analytics',
      'Priority support',
      'Custom branding',
      'Export to CSV/Excel'
    ],
    highlighted: false,
    badge: 'Save 17%'
  }
];

const Account = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('profile');

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Cancel subscription state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile state
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    company: '',
    bio: ''
  });
  const [profileErrors, setProfileErrors] = useState({});

  // Billing state
  const [currentPlan, setCurrentPlan] = useState('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month'); // 'month' or 'year'
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);

  // Notification state
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    response_alerts: true,
    weekly_reports: false,
    marketing_emails: false,
    browser_notifications: true,
    form_completion_alerts: true,
    email_frequency: 'realtime'
  });

  // Security state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Load data on mount and check for section query param
  useEffect(() => {
    loadProfileData();

    // Check if section is specified in URL
    const urlParams = new URLSearchParams(window.location.search);
    const section = urlParams.get('section');
    if (section === 'plan') {
      setActiveSection('plan');
    }
  }, [user]);

  useEffect(() => {
    if (activeSection === 'plan') {
      loadBillingData();
    } else if (activeSection === 'notifications') {
      loadNotificationSettings();
    } else if (activeSection === 'security') {
      loadSecurityData();
    }
  }, [activeSection]);

  // ====================
  // API CALLS
  // ====================

  const loadProfileData = async () => {
    setLoading(true);
    try {
      // User data is already in context, just populate form
      if (user) {
        setProfileData({
          full_name: user.full_name || '',
          email: user.email || '',
          company: user.company || '',
          bio: user.bio || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBillingData = async () => {
    setBillingLoading(true);
    try {
      // Fetch subscription and history in parallel
      const [subscriptionResponse, historyResponse] = await Promise.all([
        api.get('/billing/subscription'),
        api.get('/billing/history')
      ]);

      if (subscriptionResponse.success) {
        setCurrentPlan(subscriptionResponse.data.plan_id || 'free');
        setSubscriptionStatus(subscriptionResponse.data.status || 'active');
        setSubscriptionEndsAt(subscriptionResponse.data.ends_at);
      } else {
        setCurrentPlan('free');
      }

      if (historyResponse.success) {
        setBillingHistory(historyResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      setCurrentPlan('free');
      setBillingHistory([]);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (planId === currentPlan) return;

    // Prevent multiple simultaneous requests
    if (upgradingPlanId) {
      toast.error('Please wait for the current request to complete');
      return;
    }

    if (planId === 'free') {
      // Downgrade to free - cancel subscription
      try {
        setUpgradingPlanId(planId);
        const response = await api.post('/billing/cancel');
        if (response.success) {
          toast.success('Subscription cancelled. You will be downgraded to Free at the end of your billing period.');
          setCurrentPlan('free');
        }
      } catch (error) {
        toast.error(error.message || 'Failed to cancel subscription');
      } finally {
        setUpgradingPlanId(null);
      }
    } else {
      // Upgrade to paid plan - initialize Paystack payment
      try {
        setUpgradingPlanId(planId);
        setBillingLoading(true);
        const response = await api.post('/billing/initialize', { planId });

        if (response.success && response.data.authorization_url) {
          // Redirect to Paystack checkout
          window.location.href = response.data.authorization_url;
        } else {
          toast.error('Failed to initialize payment');
          setUpgradingPlanId(null);
          setBillingLoading(false);
        }
      } catch (error) {
        toast.error(error.message || 'Failed to initialize payment');
        setUpgradingPlanId(null);
        setBillingLoading(false);
      }
    }
  };

  // Check for payment success callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const reference = urlParams.get('reference');

    if (paymentStatus === 'success' && reference) {
      // Switch to billing section immediately
      setActiveSection('plan');

      // Verify payment
      const verifyPayment = async () => {
        try {
          const response = await api.get(`/billing/verify/${reference}`);
          if (response.success) {
            toast.success('Payment successful! Your plan has been upgraded.');
            setCurrentPlan(response.data.plan);
            // Reload billing data to show updated status
            loadBillingData();
          }
        } catch (error) {
          toast.error('Payment verification failed. Please contact support.');
        }
        // Clean up URL
        window.history.replaceState({}, '', '/dashboard/account');
      };
      verifyPayment();
    } else if (paymentStatus === 'success') {
      setActiveSection('plan');
      toast.success('Payment successful!');
      loadBillingData();
      window.history.replaceState({}, '', '/dashboard/account');
    }
  }, []);

  const loadNotificationSettings = async () => {
    try {
      const response = await api.get('/users/notification-settings');
      if (response.success) {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
  };

  const loadSecurityData = async () => {
    setSessionsLoading(true);
    try {
      const response = await api.get('/users/sessions');
      if (response.success) {
        setSessions(response.data || []);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  // ====================
  // HANDLERS
  // ====================

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const errors = {};
    if (!profileData.full_name.trim()) {
      errors.full_name = 'Name is required';
    }
    if (!profileData.email.trim()) {
      errors.email = 'Email is required';
    }

    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/users/profile', {
        full_name: profileData.full_name,
        company: profileData.company,
        bio: profileData.bio
      });

      if (response.success) {
        // Update context with new profile data
        updateUser(response.profile);
        toast.success('Profile updated successfully!');
        setProfileErrors({});
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const errors = {};
    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }
    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters';
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/users/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      if (response.success) {
        toast.success('Password updated successfully!');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setPasswordErrors({});
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/users/notification-settings', notifications);
      if (response.success) {
        toast.success('Notification preferences saved!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      const response = await api.delete(`/users/sessions/${sessionId}`);
      if (response.success) {
        setSessions(sessions.filter(s => s.id !== sessionId));
        toast.success('Session revoked successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to revoke session');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await api.delete('/users/account', {
        data: { confirmation: deleteConfirmation }
      });

      if (response.success) {
        toast.success('Account deleted successfully');
        clearTokens();
        navigate('/');
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const response = await api.post('/billing/cancel');
      if (response.success) {
        toast.success('Subscription cancelled. You will be downgraded to Free at the end of your billing period.');
        setSubscriptionStatus('cancelled');
        setShowCancelModal(false);
        loadBillingData();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  // ====================
  // SKELETON LOADERS
  // ====================

  const ProfileSkeleton = () => (
    <div className="animate-pulse">
      <div className="bg-white rounded-2xl p-6 border border-gray-100">
        <div className="h-6 bg-gray-200 rounded w-40 mb-6"></div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="h-4 bg-gray-200 rounded w-20 mb-3"></div>
              <div className="h-12 bg-gray-200 rounded-xl"></div>
            </div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
              <div className="h-12 bg-gray-200 rounded-xl"></div>
            </div>
          </div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
            <div className="h-12 bg-gray-200 rounded-xl"></div>
          </div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
            <div className="h-24 bg-gray-200 rounded-xl"></div>
          </div>
          <div className="h-12 bg-gray-200 rounded-xl w-32"></div>
        </div>
      </div>
    </div>
  );

  const BillingSkeleton = () => (
    <div className="space-y-8 animate-pulse">
      <div className="bg-white rounded-2xl p-6 border border-gray-100">
        <div className="h-6 bg-gray-200 rounded w-32 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="p-6 bg-gray-50 rounded-2xl">
              <div className="h-6 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="space-y-3">
                {[1, 2, 3, 4].map(j => (
                  <div key={j} className="h-4 bg-gray-200 rounded w-full"></div>
                ))}
              </div>
              <div className="h-12 bg-gray-200 rounded-xl mt-6"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Account Settings</h1>
        <p className="text-gray-600 text-lg">Manage your profile, plan, and preferences</p>
      </div>

      {/* Section Navigation */}
      <div className="bg-white rounded-2xl border border-gray-100 backdrop-blur-sm p-2">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'plan', label: 'Plan & Billing', icon: CreditCard },
            
            { id: 'security', label: 'Security', icon: Shield },
          ].map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                activeSection === section.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <section.icon size={20} />
              {section.label}
            </button>
          ))}
        </div>
      </div>

      {/* PROFILE SECTION */}
      {activeSection === 'profile' && (
        <>
          {loading ? (
            <ProfileSkeleton />
          ) : (
            <div className="bg-white rounded-2xl p-6 border border-gray-100 backdrop-blur-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h3>
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={profileData.full_name}
                      onChange={(e) => {
                        setProfileData({ ...profileData, full_name: e.target.value });
                        setProfileErrors({ ...profileErrors, full_name: '' });
                      }}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                        profileErrors.full_name ? 'border-red-500' : 'border-transparent'
                      }`}
                      placeholder="Enter your full name"
                    />
                    {profileErrors.full_name && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {profileErrors.full_name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      disabled
                      className="w-full px-4 py-3 bg-gray-100 border-2 border-transparent rounded-xl cursor-not-allowed text-gray-600"
                      placeholder="your.email@example.com"
                    />
                    <p className="text-xs text-gray-500 mt-2">Email cannot be changed</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    value={profileData.company}
                    onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Bio
                  </label>
                  <textarea
                    rows="4"
                    value={profileData.bio}
                    onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none transition-all duration-300"
                    placeholder="Tell us about yourself..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </form>
            </div>
          )}
        </>
      )}

      {/* PLAN & BILLING SECTION */}
      {activeSection === 'plan' && (
        <>
          {billingLoading ? (
            <BillingSkeleton />
          ) : (
            <div className="space-y-8">
              {/* Plans */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 backdrop-blur-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Choose Your Plan</h3>

                {/* Billing Interval Toggle */}
                <div className="flex justify-center mb-8">
                  <div className="relative inline-flex items-center bg-gray-100 rounded-full p-1">
                    {/* Sliding background */}
                    <div
                      className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-gray-900 rounded-full transition-all duration-300 ease-in-out ${
                        billingInterval === 'month' ? 'left-1' : 'left-[calc(50%+4px-1px)]'
                      }`}
                    />

                    {/* Monthly button */}
                    <button
                      onClick={() => setBillingInterval('month')}
                      className={`relative z-10 px-8 py-2.5 text-sm font-semibold rounded-full transition-colors duration-300 ${
                        billingInterval === 'month'
                          ? 'text-white'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Monthly
                    </button>

                    {/* Yearly button */}
                    <button
                      onClick={() => setBillingInterval('year')}
                      className={`relative z-10 px-8 py-2.5 text-sm font-semibold rounded-full transition-colors duration-300 ${
                        billingInterval === 'year'
                          ? 'text-white'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Annually
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {BILLING_PLANS.filter(plan =>
                    plan.showInBoth || plan.interval === billingInterval
                  ).map((plan) => {
                    const isCurrentPlan = currentPlan === plan.id;
                    const isCreatorPlan = plan.id.includes('creator');

                    return (
                      <div
                        key={plan.id}
                        className={`relative p-6 rounded-2xl border-2 transition-all duration-300 ${
                          plan.highlighted
                            ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100'
                            : isCurrentPlan
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        {/* Badge */}
                        {plan.badge && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                              {plan.badge}
                            </span>
                          </div>
                        )}

                        {plan.highlighted && !plan.badge && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                              <Crown size={12} />
                              Popular
                            </span>
                          </div>
                        )}

                        {/* Plan Header */}
                        <div className="mb-4">
                          <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            {plan.name}
                            {isCurrentPlan && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                Current
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                        </div>

                        {/* Price */}
                        <div className="mb-6">
                          <span className="text-4xl font-bold text-gray-900">
                            {plan.price === 0 ? 'Free' : `₦${plan.price.toLocaleString()}`}
                          </span>
                          {plan.price > 0 && (
                            <span className="text-gray-600">
                              /{plan.interval}
                            </span>
                          )}
                        </div>

                        {/* Features */}
                        <ul className="space-y-3 mb-6">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle size={16} className={`${
                                isCreatorPlan ? 'text-blue-600' : 'text-green-600'
                              }`} />
                              {feature}
                            </li>
                          ))}
                        </ul>

                        {/* Action Button */}
                        <button
                          onClick={() => handleSelectPlan(plan.id)}
                          disabled={isCurrentPlan || upgradingPlanId === plan.id}
                          className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                            isCurrentPlan || upgradingPlanId === plan.id
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : plan.highlighted
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-900 text-white hover:bg-gray-800'
                          }`}
                        >
                          {upgradingPlanId === plan.id ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 size={16} className="animate-spin" />
                              Processing...
                            </span>
                          ) : isCurrentPlan ? (
                            'Current Plan'
                          ) : plan.id === 'free' ? (
                            'Downgrade'
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              <Zap size={16} />
                              Upgrade
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Current Subscription Status */}
              {currentPlan !== 'free' && subscriptionEndsAt && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100 backdrop-blur-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Current Subscription</h3>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Crown size={24} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {BILLING_PLANS.find(p => p.id === currentPlan)?.name || 'Creator'} Plan
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Calendar size={14} />
                          {subscriptionStatus === 'cancelled'
                            ? `Expires on ${new Date(subscriptionEndsAt).toLocaleDateString()}`
                            : `Renews on ${new Date(subscriptionEndsAt).toLocaleDateString()}`
                          }
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                      subscriptionStatus === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {subscriptionStatus === 'active' ? 'Active' : 'Cancelled'}
                    </span>
                  </div>

                  {/* Cancel Subscription Button */}
                  {subscriptionStatus === 'active' && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => setShowCancelModal(true)}
                        className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-500 transition-colors duration-200"
                      >
                        <XCircle size={16} />
                        Cancel subscription
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Billing History */}
              <div className="bg-white rounded-2xl border border-gray-100 backdrop-blur-sm overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-100">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Billing History</h3>
                </div>
                {billingHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">Date</th>
                          <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">Description</th>
                          <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">Amount</th>
                          <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {billingHistory.map((item) => (
                          <tr key={item.id} className="transition-all duration-300 hover:bg-gray-50">
                            <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">
                                {new Date(item.date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">{item.description}</div>
                            </td>
                            <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                              <div className="font-semibold text-xs sm:text-sm text-gray-900">
                                ₦{item.amount.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                              <span className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${
                                item.status === 'success'
                                  ? 'bg-green-100 text-green-700'
                                  : item.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 px-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Receipt size={32} className="text-gray-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">No billing history</h4>
                    <p className="text-gray-600 text-center text-sm">
                      Your payment history will appear here once you upgrade to a paid plan
                    </p>
                  </div>
                )}
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Need help choosing?</h4>
                    <p className="text-sm text-gray-600">
                      Start with the Free plan and upgrade anytime as your needs grow. All plans include core features with no hidden fees.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      
      {/* SECURITY SECTION */}
      {activeSection === 'security' && (
        <div className="space-y-8">
          

          {/* Active Sessions */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 backdrop-blur-sm">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Active Sessions</h3>
            {sessionsLoading ? (
              <div className="space-y-4 animate-pulse">
                {[1, 2].map(i => (
                  <div key={i} className="p-6 bg-gray-100 rounded-2xl">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-48 mb-3"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                ))}
              </div>
            ) : sessions.length > 0 ? (
              <div className="space-y-4">
                {sessions.map((session, index) => (
                  <div key={session.id || index} className="flex items-start justify-between p-6 bg-gray-50 rounded-2xl">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        {session.is_current ? 'Current Session' : session.device || 'Unknown Device'}
                      </p>
                      <p className="text-gray-600 mt-2">
                        {session.browser} on {session.os} • {session.location}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Last active: {session.is_current ? 'Just now' : session.last_active}
                      </p>
                    </div>
                    {session.is_current ? (
                      <span className="inline-block px-4 py-2 text-sm font-semibold rounded-full bg-green-100 text-green-700">
                        Active
                      </span>
                    ) : (
                      <button
                        onClick={() => handleRevokeSession(session.id)}
                        className="text-red-500 hover:text-red-600 font-semibold transition-colors duration-300"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
                {sessions.length > 1 && (
                  <button className="mt-6 text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-300">
                    Sign out all other sessions
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-6 bg-gray-50 rounded-2xl">
                <Shield size={48} className="text-gray-400 mb-4" />
                <h4 className="text-xl font-semibold text-gray-900 mb-2">No Other Sessions</h4>
                <p className="text-gray-600 text-center">
                  This is your only active session
                </p>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-2xl p-6 border-2 border-red-200 backdrop-blur-sm">
            <h3 className="text-xl font-semibold text-red-900 mb-3">Danger Zone</h3>
            <p className="text-gray-600 mb-6">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-8 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-300 font-semibold"
            >
              Delete Account
            </button>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        showCloseButton={false}
        closeOnBackdrop={false}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Cancel Subscription</h3>
            <button
              onClick={() => setShowCancelModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-sm text-yellow-800">
                Your subscription will remain active until the end of your current billing period. After that, you'll be downgraded to the Free plan with limited features.
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">You'll lose access to:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <XCircle size={14} className="text-red-400" />
                  Unlimited forms
                </li>
                <li className="flex items-center gap-2">
                  <XCircle size={14} className="text-red-400" />
                  Unlimited responses
                </li>
                <li className="flex items-center gap-2">
                  <XCircle size={14} className="text-red-400" />
                  Advanced analytics
                </li>
                <li className="flex items-center gap-2">
                  <XCircle size={14} className="text-red-400" />
                  Priority support
                </li>
              </ul>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCancelling ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Account Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteConfirmation('');
        }}
        showCloseButton={false}
        closeOnBackdrop={false}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-red-600">Delete Account</h3>
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmation('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This action is permanent and cannot be undone. All your forms, responses, and data will be permanently deleted.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <span className="font-bold">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Type DELETE"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation !== 'DELETE' || isDeleting}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Account;
